import { AdminList, AdminDataGrid } from './shared';
import { useState, useEffect } from "react";
import {
  SelectInput,
  FunctionField,
  TextField,
  DateField,
  NumberField,
  useNotify,
  useRecordContext,
} from "react-admin";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField as Input,
  Typography,
  Alert,
  Box,
  Card,
  CardContent,
  Link,
} from "@mui/material";
import { apiClient } from "../providers/apiClient";
import { UserSelectInput } from "./shared/UserSelectInput";

type Operations = {
  storageBudgetBytes: number;
  accountedBytes: number;
  pendingNotices: number;
  pendingExportNotices: number;
  cleanupFailures: number;
  failedUploads: number;
  cleanup: { count: number; bytes: number; oldestAt: string | null };
  exports: { status: string; count: number; oldestAt: string | null }[];
  uploads: { status: string; count: number }[];
  lastJobs: { jobName: string; status: string; startedAt: string }[];
};
function MemoryOperations() {
  const [data, setData] = useState<Operations | null>(null);
  const [error, setError] = useState(false);
  const load = () => {
    setError(false);
    void apiClient
      .get<Operations>("/admin/photo-exports/operations")
      .then(setData)
      .catch(() => setError(true));
  };
  useEffect(load, []);
  return (
    <Card sx={{ mb: 2 }} data-testid="memory-operations">
      <CardContent>
        <Typography variant="h6">
          Memory storage and background operations
        </Typography>
        <Button onClick={load}>Refresh operations</Button>
        {error && (
          <Alert severity="error">Operations could not be loaded.</Alert>
        )}
        {data && (
          <>
            {data.accountedBytes >= data.storageBudgetBytes * 0.9 && (
              <Alert severity="warning">
                Storage is approaching its configured budget. Review pending
                uploads, archives and cleanup before increasing it.
              </Alert>
            )}
            <Typography>
              Application-accounted storage:{" "}
              {(data.accountedBytes / 1024 ** 2).toFixed(2)} MiB /{" "}
              {(data.storageBudgetBytes / 1024 ** 3).toFixed(2)} GiB budget
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Includes reserved uploads, archives and pending deletion. This is
              application inventory, not Cloudflare billing.
            </Typography>
            <Typography>
              Cleanup backlog: {data.cleanup.count} objects ·{" "}
              {(data.cleanup.bytes / 1024 ** 2).toFixed(2)} MiB · oldest{" "}
              {data.cleanup.oldestAt
                ? new Date(data.cleanup.oldestAt).toLocaleString()
                : "none"}
            </Typography>
            <Typography>
              Cleanup failures awaiting retry: {data.cleanupFailures} · pending
              uploads with verification failures: {data.failedUploads} · pending
              notices: {data.pendingNotices} · archive notifications awaiting
              delivery: {data.pendingExportNotices}
            </Typography>
            <Typography>
              Uploads:{" "}
              {data.uploads
                .map((row) => `${row.status}: ${row.count}`)
                .join(" · ") || "none"}
            </Typography>
            <Typography>
              Archives:{" "}
              {data.exports
                .map(
                  (row) =>
                    `${row.status}: ${row.count}${row.status === "queued" && row.oldestAt ? ` (oldest ${new Date(row.oldestAt).toLocaleString()})` : ""}`,
                )
                .join(" · ") || "none"}
            </Typography>
            {data.lastJobs
              .filter(
                (row, index, rows) =>
                  rows.findIndex((other) => other.jobName === row.jobName) ===
                  index,
              )
              .map((row) => (
                <Typography key={row.jobName}>
                  {row.jobName}: {row.status} ·{" "}
                  {new Date(row.startedAt).toLocaleString()}
                </Typography>
              ))}
            <Link href="/cron-jobs">View cron execution history</Link>
          </>
        )}
      </CardContent>
    </Card>
  );
}
const statuses = [
  "queued",
  "running",
  "ready",
  "failed",
  "revoked",
  "expired",
].map((id) => ({ id, name: id }));
export function PhotoArchives() {
  return (
    <Box>
      <MemoryOperations />
      <AdminList
        title="Photo Archives"
        perPage={25}
        sort={{ field: "createdAt", order: "DESC" }}
        exporter={false}
        filters={[
          <UserSelectInput key="owner" source="userId" label="Owner" alwaysOn />,
          <SelectInput
            key="status"
            source="status"
            choices={statuses}
            alwaysOn
          />,
          <SelectInput
            key="scope"
            source="scope"
            choices={[
              { id: "all-own-photos", name: "All own photos" },
              { id: "previous-relationship", name: "Previous relationship" },
            ]}
          />,
        ]}
      >
        <AdminDataGrid bulkActionButtons={false} rowClick={false}>
          <TextField source="id" />
          <TextField source="userId" label="Owner" />
          <TextField source="requestedBy" label="Requester" />
          <TextField source="scope" sortable={false} />
          <TextField source="status" />
          <FunctionField
            source="photoCount"
            label="Photo count"
            render={(row: { photoCount: number | null }) =>
              row.photoCount ?? "Unknown"
            }
          />
          <NumberField source="bytes" sortable={false} />
          <NumberField source="attempts" />
          <DateField source="leaseUntil" showTime sortable={false} />
          <DateField source="notifiedAt" showTime sortable={false} />
          <DateField source="createdAt" showTime />
          <DateField source="notBefore" showTime />
          <DateField source="completedAt" showTime />
          <DateField source="expiresAt" showTime />
          <TextField source="errorCode" sortable={false} />
        </AdminDataGrid>
      </AdminList>
    </Box>
  );
}
export function GeneratePhotoExport() {
  const user = useRecordContext();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!user) return null;
  const submit = async () => {
    setBusy(true);
    try {
      await apiClient.post("/admin/photo-exports", {
        userId: user.id,
        override,
        reason: reason.trim() || undefined,
      });
      notify("Export requested. The user will receive a notification.", {
        type: "success",
      });
      setOpen(false);
    } catch {
      notify("Export could not be requested. Check cooldown and active jobs.", {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button
        onClick={() => {
          setReason("");
          setOverride(false);
          setOpen(true);
        }}
      >
        Generate photo export
      </Button>
      <Dialog
        open={open}
        onClose={() => {
          if (!busy) setOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Generate photo export for {user.name ?? user.id}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Only this user’s own optimized photos are included. Archives are
            delivered to the user, not shown in administration.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={override}
                onChange={(_, checked) => setOverride(checked)}
                disabled={busy}
              />
            }
            label="Override the 30-day generation cooldown"
          />
          {override && (
            <Input
              label="Audit reason (at least 10 characters)"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              fullWidth
              multiline
              inputProps={{ maxLength: 300 }}
              disabled={busy}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || (override && reason.trim().length < 10)}
          >
            Generate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
